import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatUKDate } from "@/lib/date";
import { prizeSplitSlices } from "@/lib/payouts";
import { Header } from "@/components/Header";
import GameweekBanner from "@/components/GameweekBanner";
import { useScopedPath, useSlugScope } from "@/lib/scoped";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { Trophy, CalendarDays, Globe2, ArrowUp, ArrowDown, Minus, TrendingUp, Crown, Gift, Users, ChevronDown } from "lucide-react";
import { todayISO } from "@/lib/constants";

function formatPounds(pence) {
  if (!pence) return "£0";
  // Drop trailing .00 for whole-pound values (iter91 UX polish — match
  // login chip convention).
  return `£${(pence / 100).toFixed(2).replace(/\.00$/, "")}`;
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-5 h-11 rounded-sm border text-sm font-semibold tracking-tight transition-colors duration-150 active:scale-[0.98] ${
        active
          ? "bg-zinc-950 text-white border-zinc-950"
          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950 hover:text-zinc-950"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function MovementBadge({ value, testId }) {
  if (value > 0) {
    return (
      <span
        data-testid={testId}
        data-direction="up"
        className="inline-flex items-center gap-0.5 text-[11px] font-bold leading-none px-1.5 py-1 rounded-sm bg-emerald-50 text-emerald-700"
        title={`Climbed ${value} ${value === 1 ? "place" : "places"} since yesterday`}
        aria-label={`Up ${value}`}
      >
        <ArrowUp className="h-3 w-3" />{value}
      </span>
    );
  }
  if (value < 0) {
    return (
      <span
        data-testid={testId}
        data-direction="down"
        className="inline-flex items-center gap-0.5 text-[11px] font-bold leading-none px-1.5 py-1 rounded-sm bg-rose-50 text-rose-700"
        title={`Dropped ${Math.abs(value)} ${Math.abs(value) === 1 ? "place" : "places"} since yesterday`}
        aria-label={`Down ${Math.abs(value)}`}
      >
        <ArrowDown className="h-3 w-3" />{Math.abs(value)}
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      data-direction="same"
      className="inline-flex items-center text-[11px] font-bold leading-none px-1.5 py-1 rounded-sm bg-zinc-100 text-zinc-500"
      title="No change since yesterday"
      aria-label="No change"
    >
      <Minus className="h-3 w-3" />
    </span>
  );
}

export default function LeaderboardPage() {
  const scope = useScopedPath();
  const slug = useSlugScope();
  // iter151 — every leaderboard fetch below appends `?scope_slug=<slug>`
  // when inside a `/c/<slug>/*` surface so super-admins see only that
  // tenant's league. No slug (master `/leaderboard`) keeps legacy
  // "own pub_id" behaviour.
  const slugQuery = slug ? `&scope_slug=${encodeURIComponent(slug)}` : "";
  const slugParam = slug ? `?scope_slug=${encodeURIComponent(slug)}` : "";
  const { user } = useAuth();
  // iter87 — URL-slug branding is the source of truth for the Prize Pool
  // banner (mirrors the client logo). `brand.prize_fund_pence` /
  // `brand.prize_pool_text` come from `/pubs/by-slug/{slug}`. When the pub
  // has a local prize we show it; otherwise we fall back to the Dugsy-
  // global season prize from `/pool-config` (`end_of_league_prize_pence`).
  // No toggle — presence of a prize is what gates visibility.
  const { brand } = useBrand();
  const [mode, setMode] = useState("daily"); // 'daily' | 'overall' | 'league'
  const [date, setDate] = useState(todayISO());
  const [dateInitialised, setDateInitialised] = useState(false);
  const [rows, setRows] = useState([]);
  const [dailyForLastScored, setDailyForLastScored] = useState([]); // daily snapshot for the most recent scored date — drives movement arrows
  const [loading, setLoading] = useState(true);
  const [seasonPrizePence, setSeasonPrizePence] = useState(0);
  const [poolConfig, setPoolConfig] = useState(null);

  // Mini-leagues the user is in — populated lazily when 'My leagues' is opened
  const [myLeagues, setMyLeagues] = useState([]);
  const [myLeaguesLoaded, setMyLeaguesLoaded] = useState(false);
  const [selectedLeagueId, setSelectedLeagueId] = useState("");
  const [leagueMenuOpen, setLeagueMenuOpen] = useState(false);
  const leagueMenuRef = useRef(null);

  // Season selector — defaults to current active so the existing flow is preserved.
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [archive, setArchive] = useState(null); // { standings, champion, prize_pence, closed_at } when a closed season is picked

  const selectedSeason = useMemo(
    () => seasons.find((s) => s.id === selectedSeasonId) || null,
    [seasons, selectedSeasonId],
  );

  // iter87 — resolve the effective Prize Pool banner values entirely from
  // the URL-slug brand FIRST (local pub prize), then fall back to the
  // Dugsy-global season prize. No toggle logic — visibility is determined
  // by the presence of a prize (fund > 0 OR text set).
  // iter91 — Prize banner data comes exclusively from the URL-slug brand
  // (see `PUT /pool-config?as_pub=<slug>` + `PUT /season-prize-config?as_pub=<slug>`
  // mirrors on the backend). No toggle logic — visibility is determined by
  // whether ANY value is set (match-day / season / voucher). Global
  // pool_config is only used as a last-resort fallback for the
  // Dugsy-wide `/leaderboard` (no slug) surface.
  const effectivePrize = useMemo(() => {
    const matchDayPence = Number(brand?.current_match_day_prize_pence || 0);
    const rawSeasonPence = Number(brand?.prize_fund_pence || 0);
    const voucherCount = Number(brand?.voucher_count || 0);
    const voucherText = (brand?.prize_pool_text || "").trim() || null;
    const voucherExpiry = (brand?.voucher_expires_at || "").trim() || null;
    // iter147 — % of entry fees mode. When "percent" the projected pot
    // comes from `projected_season_prize_pence` (computed server-side).
    const seasonPrizeType = (brand?.season_prize_type || "fixed").trim();
    const seasonPrizePercent = Number(brand?.season_prize_percent || 0);
    const projectedSeasonPence = Number(brand?.projected_season_prize_pence || 0);
    const seasonPence =
      seasonPrizeType === "percent" ? projectedSeasonPence : rawSeasonPence;
    const hasSeasonPrize =
      seasonPrizeType === "percent" ? seasonPrizePercent > 0 : rawSeasonPence > 0;
    // iter148 — vouchers only surface when BOTH text is set AND count > 0.
    const hasVoucherPrize = !!voucherText && voucherCount > 0;
    if (matchDayPence > 0 || hasSeasonPrize || hasVoucherPrize) {
      return {
        source: "pub",
        matchDayPence,
        seasonPence,
        seasonPrizeType,
        seasonPrizePercent,
        voucherCount,
        voucherText,
        voucherExpiry,
      };
    }
    // Fallback: Dugsy-global season prize when there's no brand context
    // (bare `/leaderboard` on the master surface).
    if (seasonPrizePence > 0) {
      return {
        source: "global",
        matchDayPence: 0,
        seasonPence: seasonPrizePence,
        seasonPrizeType: "fixed",
        seasonPrizePercent: 0,
        voucherCount: 0,
        voucherText: null,
        voucherExpiry: null,
      };
    }
    return {
      source: "none",
      matchDayPence: 0,
      seasonPence: 0,
      seasonPrizeType: "fixed",
      seasonPrizePercent: 0,
      voucherCount: 0,
      voucherText: null,
      voucherExpiry: null,
    };
  }, [brand, seasonPrizePence]);
  const isArchive = selectedSeason?.status === "closed";
  const selectedLeague = useMemo(
    () => myLeagues.find((l) => l.id === selectedLeagueId) || null,
    [myLeagues, selectedLeagueId],
  );

  const loadMyLeagues = useCallback(async () => {
    try {
      const { data } = await api.get("/leagues/mine");
      setMyLeagues(Array.isArray(data) ? data : []);
      setMyLeaguesLoaded(true);
    } catch (e) {
      toast.error(formatApiError(e));
      // Leave myLeaguesLoaded=false so the NEXT open retries the fetch
      // instead of perma-locking on an empty menu after a transient failure.
    }
  }, []);

  // Close the league dropdown on outside click / escape
  useEffect(() => {
    if (!leagueMenuOpen) return undefined;
    const onClick = (e) => {
      if (leagueMenuRef.current && !leagueMenuRef.current.contains(e.target)) {
        setLeagueMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setLeagueMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [leagueMenuOpen]);

  const openLeagueMenu = useCallback(() => {
    if (!myLeaguesLoaded) loadMyLeagues();
    setLeagueMenuOpen((o) => !o);
  }, [myLeaguesLoaded, loadMyLeagues]);

  const pickLeague = useCallback((leagueId) => {
    setSelectedLeagueId(leagueId);
    setMode("league");
    setLeagueMenuOpen(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, ss, cur] = await Promise.all([
          api.get("/pool-config"),
          api.get(`/seasons${slugParam}`),
          api.get("/seasons/current"),
        ]);
        if (cancelled) return;
        setSeasonPrizePence(Number(cfg.data?.end_of_league_prize_pence) || 0);
        setPoolConfig(cfg.data || null);
        setSeasons(Array.isArray(ss.data) ? ss.data : []);
        setSelectedSeasonId(cur.data?.id || "");
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [slugParam]);

  // On first mount, default the Daily league date to the most recent match-day
  // that has at least one finished match. This means the page lands on the last
  // completed match-day's standings rather than today (which often has no scored
  // matches yet). The user can still change it via the date picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/leaderboard/last-scored-date${slugParam}`);
        if (!cancelled && data?.date) setDate(data.date);
      } catch (err) {
        // Non-fatal — keep today's date as fallback if the last-scored-date lookup fails.
        if (typeof console !== "undefined") console.debug("[leaderboard] last-scored-date fetch failed:", err?.message);
      } finally {
        if (!cancelled) setDateInitialised(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slugParam]);

  // When a CLOSED season is selected, fetch and render its archived snapshot
  // instead of the live daily/overall views.
  useEffect(() => {
    let cancelled = false;
    if (!isArchive || !selectedSeasonId) {
      setArchive(null);
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/seasons/${selectedSeasonId}/leaderboard${slugParam}`);
        if (!cancelled) setArchive(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isArchive, selectedSeasonId, slugParam]);

  const load = useCallback(async () => {
    if (isArchive) return; // archive has its own loader
    setLoading(true);
    try {
      if (mode === "daily") {
        const { data } = await api.get(`/leaderboard?date=${date}${slugQuery}`);
        setRows(data);
      } else if (mode === "league") {
        if (!selectedLeagueId) {
          setRows([]);
        } else {
          const { data } = await api.get(`/leagues/${selectedLeagueId}/leaderboard`);
          setRows(Array.isArray(data) ? data : []);
        }
      } else {
        // Overall mode — fetch overall + the daily snapshot for the most-recent scored date
        // so we can compute movement arrows accurately even when "today" has no scored matches.
        const lastRes = await api.get(`/leaderboard/last-scored-date${slugParam}`);
        const lastDate = lastRes.data?.date;
        const dailyPromise = lastDate
          ? api.get(`/leaderboard?date=${lastDate}${slugQuery}`)
          : Promise.resolve({ data: [] });
        const [overallRes, dailyRes] = await Promise.all([
          api.get(`/leaderboard${slugParam}`),
          dailyPromise,
        ]);
        setRows(overallRes.data);
        setDailyForLastScored(dailyRes.data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [mode, date, isArchive, selectedLeagueId, slugParam, slugQuery]);

  useEffect(() => {
    // For Overall mode the date isn't used; for Daily, wait until the
    // last-scored-date default has resolved so we don't flash today's empty
    // standings before snapping to the real last match-day.
    // Skip entirely when an archived season is selected.
    if (isArchive) return;
    if (mode !== "daily" || dateInitialised) load();
  }, [load, mode, dateInitialised, isArchive]);

  /**
   * Movement = current overall rank − previous overall rank.
   * Previous = ranking before the most recent batch of scored points
   * (i.e. subtracting each player's points earned on the last scored match_date).
   * Positive movement = climbed (↑); negative = dropped (↓); 0 = unchanged.
   */
  const movementByUser = useMemo(() => {
    if (mode !== "overall" || rows.length === 0) return {};
    const lastBatchPoints = Object.fromEntries(
      dailyForLastScored.map((r) => [r.user_id, r.total_points || 0]),
    );
    // Snapshot before the most recent scored day
    const previous = rows
      .map((r) => ({
        user_id: r.user_id,
        prev_points: (r.total_points || 0) - (lastBatchPoints[r.user_id] || 0),
        prev_matches: Math.max(
          0,
          (r.matches_played || 0) - (lastBatchPoints[r.user_id] !== undefined ? 1 : 0),
        ),
      }))
      .sort((a, b) => b.prev_points - a.prev_points || a.prev_matches - b.prev_matches);
    const prevRankByUser = Object.fromEntries(previous.map((r, i) => [r.user_id, i + 1]));
    const out = {};
    rows.forEach((r, i) => {
      const currentRank = i + 1;
      const prevRank = prevRankByUser[r.user_id] ?? currentRank;
      out[r.user_id] = prevRank - currentRank; // +ve = climbed
    });
    return out;
  }, [mode, rows, dailyForLastScored]);

  const niceDate = formatUKDate(date, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Biggest climber today: largest positive movement; tie-break by current rank (lower rank wins)
  const topClimber = useMemo(() => {
    if (mode !== "overall") return null;
    let best = null;
    rows.forEach((r, i) => {
      const m = movementByUser[r.user_id] ?? 0;
      if (m <= 0) return;
      if (!best || m > best.move || (m === best.move && i + 1 < best.rank)) {
        best = { name: r.user_name, move: m, rank: i + 1, points: r.total_points };
      }
    });
    return best;
  }, [mode, rows, movementByUser]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">League standings</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
              The league.
            </h1>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            {seasons.length > 0 && (
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Season</Label>
                <select
                  value={selectedSeasonId}
                  onChange={(e) => setSelectedSeasonId(e.target.value)}
                  data-testid="season-selector"
                  className="mt-1 h-11 w-56 rounded-sm border border-zinc-300 bg-white px-2 text-sm font-semibold"
                >
                  {seasons.map((s) => {
                    const suffix = s.status === "active"
                      ? " · live"
                      : s.closed_at
                        ? ` · closed ${formatUKDate(s.closed_at, { day: "numeric", month: "short", year: "numeric" })}`
                        : " · closed";
                    return (
                      <option key={s.id} value={s.id}>
                        {s.name}{suffix}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-500 pb-2">
              <Trophy className="h-4 w-4" /> {isArchive ? "Archived" : "Live standings"}
            </div>
          </div>
        </div>

        {/* Archived snapshot view — read-only standings + champion */}
        {isArchive ? (
          loading ? (
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
          ) : !archive ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="archive-empty">
              <p className="font-heading text-2xl font-semibold text-zinc-950">No archive for this season.</p>
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-zinc-500" data-testid="archive-subtitle">
                Final standings for <span className="font-semibold text-zinc-950">{selectedSeason?.name}</span>
                {archive.closed_at ? <> · closed {new Date(archive.closed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</> : null}
              </div>
              {archive.champion && (
                <div
                  data-testid="archive-champion-banner"
                  className="mb-6 flex items-center gap-4 px-5 py-4 rounded-sm border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50"
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shadow shrink-0">
                    <Crown className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">Champion</p>
                    <p className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-zinc-950 mt-0.5">
                      <span data-testid="archive-champion-name">{archive.champion.name}</span>
                      {" · "}
                      <span className="tabular-nums">{archive.champion.total_points} pts</span>
                    </p>
                    {archive.prize_pence > 0 && (
                      <p className="text-xs text-amber-800/80 mt-0.5 tabular-nums" data-testid="archive-prize">
                        Took home {formatPounds(archive.prize_pence)}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {(archive.standings || []).length === 0 ? (
                <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="archive-empty-standings">
                  <p className="text-zinc-500 text-sm">No entrants in this season.</p>
                </div>
              ) : (
                <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="archive-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 w-16">#</th>
                        <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Player</th>
                        <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Matches</th>
                        <th
                          className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
                          title="Number of individual stat predictions hit at the max (3 pts on goals/shots/corners/offsides/fouls, 2 pts on throw-ins). Used as the 3rd tie-break."
                        >
                          Correct
                        </th>
                        <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Total points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(archive.standings || []).map((r, i) => {
                        const isMe = user && r.user_id === user.id;
                        return (
                          <tr
                            key={r.user_id || `${r.user_name}-${i}`}
                            data-testid={`archive-row-${i + 1}`}
                            className={`border-b border-zinc-100 last:border-b-0 ${isMe ? "bg-[#0038FF]/5" : ""}`}
                          >
                            <td className="px-6 py-4 font-heading font-bold text-zinc-950 tabular-nums">
                              {i === 0 ? <span className="text-[#0038FF]">{i + 1}</span> : i + 1}
                            </td>
                            <td className="px-6 py-4 font-medium text-zinc-950">
                              {r.user_name}{isMe ? " (you)" : ""}
                            </td>
                            <td className="px-6 py-4 text-right text-zinc-700 tabular-nums">{r.matches_played}</td>
                            <td
                              className="px-6 py-4 text-right text-zinc-700 tabular-nums"
                              data-testid={`archive-row-correct-${i + 1}`}
                            >
                              {r.correct_count ?? 0}
                            </td>
                            <td className="px-6 py-4 text-right font-heading font-bold text-2xl text-zinc-950 tabular-nums">
                              {r.total_points}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )
        ) : (
          <>
            {/* Mode tabs */}
            <div className="flex flex-wrap items-end gap-3 mb-8">
          <TabButton
            active={mode === "daily"}
            onClick={() => setMode("daily")}
            icon={CalendarDays}
            label="Last match-day"
            testId="tab-daily"
          />
          <TabButton
            active={mode === "overall"}
            onClick={() => setMode("overall")}
            icon={Globe2}
            label="Overall league"
            testId="tab-overall"
          />
          {/* My leagues dropdown — picks a private mini-league to standings for */}
          <div className="relative" ref={leagueMenuRef}>
            <button
              onClick={openLeagueMenu}
              data-testid="tab-my-leagues"
              aria-haspopup="menu"
              aria-expanded={leagueMenuOpen}
              className={`inline-flex items-center gap-2 px-5 h-11 rounded-sm border text-sm font-semibold tracking-tight transition-colors duration-150 active:scale-[0.98] ${
                mode === "league"
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950 hover:text-zinc-950"
              }`}
            >
              <Users className="h-4 w-4" />
              {mode === "league" && selectedLeague ? selectedLeague.name : "My leagues"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${leagueMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {leagueMenuOpen && (
              <div
                data-testid="my-leagues-menu"
                role="menu"
                className="absolute left-0 top-full mt-1 z-20 w-72 max-h-80 overflow-y-auto bg-white border border-zinc-300 rounded-sm shadow-lg"
              >
                {!myLeaguesLoaded ? (
                  <p className="px-4 py-3 text-xs uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
                ) : myLeagues.length === 0 ? (
                  <div className="px-4 py-4" data-testid="my-leagues-menu-empty">
                    <p className="text-sm text-zinc-700 font-semibold">No leagues yet.</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      <Link to={scope("/leagues")} className="underline hover:text-zinc-950">Create or join one →</Link>
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {myLeagues.map((l) => (
                      <li key={l.id}>
                        <button
                          type="button"
                          onClick={() => pickLeague(l.id)}
                          data-testid={`my-leagues-menu-item-${l.id}`}
                          className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-3 ${
                            selectedLeagueId === l.id ? "bg-zinc-50" : ""
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block font-heading text-sm font-bold text-zinc-950 truncate">{l.name}</span>
                            <span className="block text-[11px] text-zinc-500 mt-0.5">
                              {l.member_count} {l.member_count === 1 ? "member" : "members"}
                              {l.is_owner ? " · owner" : ""}
                            </span>
                          </span>
                          <code className="bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded-sm font-mono text-[10px] tracking-widest shrink-0">
                            {l.invite_code}
                          </code>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {mode === "daily" && (
            <div className="ml-auto">
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="leaderboard-date-picker"
                className="mt-1 h-11 w-48 rounded-sm border-zinc-300"
              />
            </div>
          )}
        </div>

        <div className="mb-4 text-sm text-zinc-500" data-testid="leaderboard-subtitle">
          {mode === "daily" ? (
            <>Points earned on <span className="font-semibold text-zinc-950">{niceDate}</span></>
          ) : mode === "league" ? (
            selectedLeague ? (
              <>
                Standings for <span className="font-semibold text-zinc-950">{selectedLeague.name}</span>
                {" "}<span className="text-zinc-400">·</span>{" "}
                <span className="font-semibold text-zinc-950">{selectedLeague.member_count} {selectedLeague.member_count === 1 ? "member" : "members"}</span>
                {" "}<span className="text-zinc-400">·</span>{" "}
                <span data-testid="league-today-label" className="font-semibold text-zinc-950 tabular-nums">
                  {new Date().toLocaleDateString("en-GB")}
                </span>
              </>
            ) : (
              <>Pick a league from the <strong>My leagues</strong> menu above to see its standings.</>
            )
          ) : (
            <>
              All-time points across every match{" "}
              <span className="text-zinc-400">·</span>{" "}
              <span data-testid="overall-today-label" className="font-semibold text-zinc-950 tabular-nums">
                {new Date().toLocaleDateString("en-GB")}
              </span>
            </>
          )}
        </div>

        {/* iter143 — Gameweek pill + prize banners on ONE row at the SAME
            HEIGHT. Pill sits LEFT of the prize / climber row (user layout
            spec). `items-stretch` on the outer flex row makes the pill
            match the tallest neighbour's height. Flex-wraps to stacked
            on mobile. */}
        <div className="mb-6 flex flex-wrap items-stretch gap-3">
          <GameweekBanner variant="pill" />
          {(topClimber || effectivePrize.source !== "none") && (
            <div
              data-testid="overall-banners"
              className={`flex-1 min-w-[240px] grid gap-4 ${topClimber && effectivePrize.source !== "none" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}
            >
            {topClimber && (
              <div
                data-testid="top-climber-banner"
                className="flex items-center gap-4 px-5 py-4 rounded-sm border border-emerald-200 bg-emerald-50"
              >
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-emerald-600 text-white shrink-0">
                  <TrendingUp className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">Biggest climber</p>
                  <p className="mt-0.5 text-sm sm:text-base text-zinc-950">
                    <span className="font-heading font-bold" data-testid="top-climber-name">{topClimber.name}</span>
                    <span className="text-zinc-500"> climbed </span>
                    <span className="font-bold tabular-nums text-emerald-700" data-testid="top-climber-move">
                      {topClimber.move} {topClimber.move === 1 ? "place" : "places"}
                    </span>
                    <span className="text-zinc-500"> on the last match-day · now </span>
                    <span className="font-bold tabular-nums">#{topClimber.rank}</span>
                    <span className="text-zinc-500"> on </span>
                    <span className="font-bold tabular-nums">{topClimber.points} pts</span>
                  </p>
                </div>
              </div>
            )}
            {effectivePrize.source !== "none" && (() => {
              const hasMatchDay = effectivePrize.matchDayPence > 0;
              const hasSeason =
                effectivePrize.seasonPrizeType === "percent"
                  ? effectivePrize.seasonPrizePercent > 0
                  : effectivePrize.seasonPence > 0;
              const hasVoucher = !!effectivePrize.voucherText && effectivePrize.voucherCount > 0;
              // Legacy split breakdown (from global pool_config) still shown
              // for the master surface only. Per-pub is always single-winner.
              const slices = effectivePrize.source === "global"
                ? prizeSplitSlices(poolConfig)
                : [];
              return (
                <div
                  data-testid="season-prize-banner"
                  data-prize-source={effectivePrize.source}
                  className="relative overflow-hidden flex items-start gap-4 px-5 py-4 rounded-sm border border-emerald-300 bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50"
                >
                  <span className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-300/40 blur-3xl" aria-hidden="true" />
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-b from-emerald-500 to-emerald-700 text-white shrink-0 shadow mt-1">
                    <Crown className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 relative flex-1">
                    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                      {hasMatchDay && (
                        <div data-testid="season-prize-matchday">
                          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">Match-day prize</p>
                          <p
                            className="font-heading text-2xl sm:text-3xl font-bold tabular-nums text-emerald-900 leading-none mt-0.5"
                            data-testid="season-prize-matchday-amount"
                          >
                            {formatPounds(effectivePrize.matchDayPence)}
                          </p>
                        </div>
                      )}
                      {hasSeason && (
                        <div data-testid="season-prize-season">
                          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">Current league prize</p>
                          <div className="flex items-baseline gap-3 flex-wrap mt-0.5">
                            <p
                              className="font-heading text-2xl sm:text-3xl font-bold tabular-nums text-emerald-900 leading-none"
                              data-testid="season-prize-amount"
                            >
                              {formatPounds(effectivePrize.seasonPence)}
                            </p>
                            {effectivePrize.seasonPrizeType === "percent" && (
                              <p
                                className="font-heading text-base sm:text-lg font-bold text-emerald-900 leading-none"
                                data-testid="season-prize-percent"
                              >
                                ({effectivePrize.seasonPrizePercent}% of entry fees)
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {hasVoucher && (
                        <div data-testid="season-prize-voucher">
                          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 inline-flex items-center gap-1">
                            <Gift className="h-3 w-3" /> Vouchers
                          </p>
                          <p className="font-heading text-base sm:text-lg font-bold text-emerald-900 leading-tight mt-0.5">
                            {effectivePrize.voucherCount > 0 ? `${effectivePrize.voucherCount} × ` : ""}{effectivePrize.voucherText}
                          </p>
                          {effectivePrize.voucherExpiry && (
                            <p className="text-[10px] font-medium text-emerald-700 mt-0.5" data-testid="season-prize-voucher-expiry">
                              expires {formatUKDate(effectivePrize.voucherExpiry, { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {slices.length > 1 && (
                      <div data-testid="season-prize-split-breakdown" className="mt-3 flex items-center gap-2 flex-wrap">
                        {slices.map((s) => (
                          <div
                            key={s.rank}
                            data-testid={`season-prize-slice-${s.rank}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-white/80 border border-emerald-300"
                          >
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">{s.label}</span>
                            <span className="font-heading text-sm font-bold tabular-nums text-emerald-900">{formatPounds(s.pence)}</span>
                            <span className="text-[10px] text-emerald-700/70 tabular-nums">{s.pct}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(hasMatchDay || hasSeason) && (
                      <p className="mt-2 text-xs text-emerald-900/80">
                        {effectivePrize.source === "pub"
                          ? `Awarded at ${brand?.name || "your pub"} — see venue for details.`
                          : `Awarded to the top of the Overall League when the season ends${slices.length === 1 ? " (winner takes all)." : ", apportioned to the top finishers."}`}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
        ) : mode === "league" && !selectedLeagueId ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="league-no-selection">
            <p className="font-heading text-2xl font-semibold text-zinc-950">Pick a league.</p>
            <p className="mt-2 text-sm text-zinc-500">
              Use the <strong>My leagues</strong> menu above to choose which mini-league&apos;s standings to view.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-12 text-center" data-testid="leaderboard-empty">
            <p className="font-heading text-2xl font-semibold text-zinc-950">No scores yet.</p>
            <p className="mt-2 text-sm text-zinc-500">
              {mode === "daily"
                ? "No predictions have been scored for this date yet."
                : mode === "league"
                  ? "No predictions from members have been scored yet."
                  : "Predictions appear here once matches are scored."}
            </p>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="leaderboard-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 w-16">#</th>
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Player</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Matches</th>
                  <th
                    className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
                    title="Number of individual stat predictions hit at the max (3 pts on goals/shots/corners/offsides/fouls, 2 pts on throw-ins). Used as the 3rd tie-break."
                  >
                    Correct
                  </th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    {mode === "daily" ? "Daily points" : "Total points"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isMe = user && r.user_id === user.id;
                  const move = mode === "overall" ? (movementByUser[r.user_id] ?? 0) : null;
                  return (
                    <tr
                      key={r.user_id}
                      data-testid={`leaderboard-row-${i + 1}`}
                      className={`border-b border-zinc-100 last:border-b-0 ${isMe ? "bg-[#0038FF]/5" : ""}`}
                    >
                      <td className="px-6 py-4 font-heading font-bold text-zinc-950 tabular-nums">
                        {i === 0 ? <span className="text-[#0038FF]">{i + 1}</span> : i + 1}
                      </td>
                      <td className="px-6 py-4 font-medium text-zinc-950">
                        <span className="inline-flex items-center gap-2">
                          {r.user_name}{isMe ? " (you)" : ""}
                          {mode === "overall" && (
                            <MovementBadge value={move} testId={`movement-${i + 1}`} />
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-zinc-700 tabular-nums">{r.matches_played}</td>
                      <td
                        className="px-6 py-4 text-right text-zinc-700 tabular-nums"
                        data-testid={`leaderboard-row-correct-${i + 1}`}
                      >
                        {r.correct_count ?? 0}
                      </td>
                      <td className="px-6 py-4 text-right font-heading font-bold text-2xl text-zinc-950 tabular-nums">
                        {r.total_points}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* end live-view branch */}
          </>
        )}

        <p className="mt-8 text-xs text-zinc-500 uppercase tracking-[0.15em]" data-testid="leaderboard-rules">
          Scoring · 3 pts exact · 1 pt within ±1 (2 pts within ±5 for throw-ins) · Ties: fewest match-days entered, then most correct predictions
        </p>
        <p className="mt-2 text-xs text-zinc-400">
          <Link to={scope("/champions")} data-testid="leaderboard-champions-link" className="hover:text-zinc-700 underline underline-offset-2">
            View the Hall of Fame · past season champions →
          </Link>
        </p>
      </main>
    </div>
  );
}
